import { useGlobalContext } from '#contexts/GlobalContext';
import { useDeleteAssign, useGetAssignsInterval } from '@vindr/services/actions/assign.action';
import { openViewer } from '@vindr/services/actions/study.action';
import { SORT_ORDER } from '@vindr/services/constants/filter.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import keycloak from '@vindr/services/keycloak';
import { Report, SidePanel, SiderLayout, Table } from '@vindr/ui';
import EditAssign from '@vindr/ui/src/components/common/Assign/EditAssign';
import useWindowSize from '@vindr/utils/hooks/useWindowSize';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AssignedHeader from './AssignedHeader';
import Filter from './Filter';
import { INITIAL_FILTER_ASSIGNED } from './constants';
import useContextMenu, { MENU_TYPE } from './useContextMenu';
import useDefinedColumns from './useDefinedColumns';

export const INITIAL_SORTER_ASSIGNED = {
  sort_field: 'created_at',
  sort_order: SORT_ORDER.DESC,
};

const Assigned = () => {
  const { width } = useWindowSize();
  const { message, modal } = useGlobalContext();
  const [editingAssign, setEditingAssign] = useState(null);
  const [isEditingAssign, setIsEditingAssign] = useState(false);
  const { mutate: deleteAssign } = useDeleteAssign();
  const { t } = useTranslation('Vindoc');
  const [collapsedRight, setCollapsedRight] = useState(window.innerWidth < 640);

  const handleRemove = (record) => {
    modal.confirm({
      className: 'delete-modal',
      title: t(MESSAGE.DIALOG.CFD_17000),
      okText: t('Delete'),
      onOk: () => {
        deleteAssign(
          { id: record?.id },
          {
            onSuccess: () => {
              message.success(t(MESSAGE.SUCCESS.SCD_17003));
            },
            onError: (error: any) => {
              const error_message =
                (error?.response?.data?.error_code &&
                  MESSAGE.ERROR[error?.response?.data?.error_code]) ||
                'Delete error';

              message.error(t(error_message));
            },
          }
        );
      },
    });
  };

  const handleEdit = (record) => {
    setEditingAssign(record);
    setIsEditingAssign(true);
  };

  const [pagination, setPagination] = useState({
    page: 1,
    size: 25,
  });
  const performedColumns = useDefinedColumns({
    onEdit: handleEdit,
    onRemove: handleRemove,
    pagination,
  });
  const [params, setParams] = useState(INITIAL_FILTER_ASSIGNED);
  const [sorter, setSorter] = useState(INITIAL_SORTER_ASSIGNED);
  // const institutions = useGetInstitutions({ offset: 0, limit: 100 })?.data?.data || [];
  const data = useGetAssignsInterval({
    offset: (pagination.page - 1) * pagination.size,
    limit: pagination.size,
    ...sorter,
    ...params,
  });
  const count = data?.data?.count || 0;
  const assigns = data?.data?.data || [];
  const isFetching = data?.isFetching;
  const isLoading = data?.isLoading;
  const refetch = data?.refetch;

  const [isRefetch, setIsRefetch] = useState(false);
  useEffect(() => {
    if (!isFetching) {
      // After fetching, set isRefetch to false;
      setIsRefetch(false);
    }
  }, [isFetching]);

  const forceSearch = (params) => {
    handleFilter(params);
    // Set refetch to true only when force search
    setIsRefetch(true);
    refetch?.();
  };

  const handleFilter = (p, type) => {
    setPagination({ ...pagination, page: 1 });
    setParams(p);

    //? on mobile, not close when choose select multiple
    if (type === 'institution_ids') return;

    setCollapsedRight(true);
  };

  const { iframe } = useGlobalContext();
  const [, setOpenIframe] = iframe;

  const openReport = useCallback((assign_id) => {
    if (!keycloak.hasOneOfPerm([PERMISSIONS.page.view_report])) return;

    setReportState({ visible: true, assign_id });
  }, []);

  const [reportState, setReportState] = useState({ visible: false, assign_id: null });
  const handleContextMenuSelect = (type, record) => {
    const noStudyMsg = 'No matched study found';

    switch (type) {
      case MENU_TYPE.OPEN_REPORT:
        openReport(record?.id);
        break;
      case MENU_TYPE.OPEN_VIEWER:
        if (!record?.order?.study?.study_instance_uid) {
          message.error(t(noStudyMsg));

          return;
        }
        openViewer(record?.order?.study?.study_instance_uid, record?.order?.procedure?.modality);
        break;
      case MENU_TYPE.OPEN_VIEWER_AND_REPORT:
        openReport(record?.id);

        if (!record?.order?.study?.study_instance_uid) {
          message.error(t(noStudyMsg));

          return;
        }
        openViewer(record?.order?.study?.study_instance_uid, record?.order?.procedure?.modality);
        break;
      case MENU_TYPE.OPEN_SPLIT_WINDOW:
        if (!record?.order?.study?.study_instance_uid) {
          message.error(t(noStudyMsg));

          return;
        }
        setOpenIframe(record?.order?.study?.study_instance_uid, record?.order?.procedure?.modality);
        break;
      case MENU_TYPE.OPEN_SPLIT_WINDOW_AND_REPORT:
        openReport(record?.id);

        if (!record?.order?.study?.study_instance_uid) {
          message.error(t(noStudyMsg));

          return;
        }
        setOpenIframe(record?.order?.study?.study_instance_uid, record?.order?.procedure?.modality);
        break;
      default:
        break;
    }
  };
  const contextMenuItems = useContextMenu({ handleContextMenuSelect });
  const filter = (
    <Filter
      onChange={handleFilter}
      initFilters={INITIAL_FILTER_ASSIGNED}
      forceSearch={forceSearch}
    />
  );

  return (
    <SiderLayout
      rightSidebar={
        width < 640 ? (
          <SidePanel
            side="right"
            defaultCollapsed={true}
            width={215}
            collapsed={collapsedRight}
            onChange={(val) => setCollapsedRight(val)}
          >
            <div className="m-2">{filter}</div>
          </SidePanel>
        ) : null
      }
    >
      <div className="flex h-full flex-col overflow-auto rounded-sm bg-background-light-3 p-4 dark:bg-background-dark-3">
        <AssignedHeader totalItem={count} />
        {width >= 640 && filter}
        <div className="flex h-0 flex-1 flex-col">
          <Table
            wrapperClassName="min-h-[210px]"
            // rowSelection={{ ...rowSelection }}
            columns={performedColumns}
            dataSource={assigns}
            // scroll={{ y: (height ?? 700) - 260 }}
            rowKey="id"
            pagination={pagination}
            loading={isLoading || isRefetch}
            count={count}
            onTableChange={(_, __, sorter) =>
              setSorter({
                sort_field: sorter.field,
                sort_order: sorter.order === 'ascend' ? 0 : 1,
              })
            }
            contextMenu={{
              items: contextMenuItems,
              onClick: (menuItem, record) => {
                if (menuItem.onClick) {
                  menuItem.onClick(record);
                }
              },
            }}
            onPaginationChange={(p) => p && setPagination(p)}
            // onChange={handleOnChangeTable}
            onRow={(record: typeof assigns[number]) => ({
              onDoubleClick: (event) => {
                event.stopPropagation();
                event.preventDefault();
                if ((event?.target as HTMLElement)?.nodeName === 'INPUT') {
                  // handle action doubleClick to checkbox
                  return;
                }
                contextMenuItems[0].onClick(record);
              },
            })}
          />
        </div>
      </div>
      {reportState?.assign_id ? (
        <Report
          visible={reportState.visible}
          assign={assigns.find((it) => it?.id === reportState.assign_id)}
          // onMissingOrder={() => {
          //   if (!matchOrderState.visible) {
          //     debounceAlert();
          //     setMatchOrderState({ visible: true, study: reportState.study });
          //   }
          // }}
          onCancel={() => setReportState({ visible: false, assign_id: null })}
        />
      ) : null}
      <EditAssign
        assignData={editingAssign}
        isEditing={isEditingAssign}
        onFinish={() => {
          setEditingAssign(null);
          setIsEditingAssign(false);
        }}
        message={message}
      />
    </SiderLayout>
  );
};

export default Assigned;
